import { Calendar, Clock, Loader2 } from "lucide-react";
import React, { FormEvent, useMemo } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Modal from "../ui/Modal";

interface SchedulePostModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  postId: number | null;
  scheduleDate: string;
  setScheduleDate: (date: string) => void;
  scheduleTime: string;
  setScheduleTime: (time: string) => void;
}

const SchedulePostModal: React.FC<SchedulePostModalProps> = ({
  open,
  onClose,
  onConfirm,
  loading,
  postId,
  scheduleDate,
  setScheduleDate,
  scheduleTime,
  setScheduleTime,
}) => {
  const { t } = useTranslation();
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onConfirm();
  };

  const minDate = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const scheduleDateTime = useMemo(() => {
    if (!scheduleDate || !scheduleTime) return null;
    try {
      const [year, month, day] = scheduleDate.split("-");
      const [hours, minutes] = scheduleTime.split(":");
      const date = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
      );
      return date;
    } catch {
      return null;
    }
  }, [scheduleDate, scheduleTime]);

  const isFutureEnough = useMemo(() => {
    if (!scheduleDateTime) return true;
    const minAllowed = new Date();
    minAllowed.setMinutes(minAllowed.getMinutes() + 5);
    return scheduleDateTime >= minAllowed;
  }, [scheduleDateTime]);

  const timeError = useMemo(() => {
    if (!scheduleDate || !scheduleTime || isFutureEnough) return "";
    return t("O horário deve ser pelo menos 5 minutos no futuro");
  }, [scheduleDate, scheduleTime, isFutureEnough, t]);

  const formattedDateTime = useMemo(() => {
    if (!scheduleDate || !scheduleTime) return null;

    try {
      const [year, month, day] = scheduleDate.split("-");
      const [hours, minutes] = scheduleTime.split(":");
      const date = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
      );

      return date.toLocaleString("pt-BR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return null;
    }
  }, [scheduleDate, scheduleTime]);

  const isValid = scheduleDate && scheduleTime && isFutureEnough;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Agendar Publicação")}
      subtitle={t("Defina o momento perfeito para seu post ir ao ar")}
      icon={<Calendar className="h-5 w-5" />}
      headerVariant="gradient"
      maxWidth="max-w-xl"
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={loading}
          >
            {t("Cancelar")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            icon={
              loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Calendar className="w-4 h-4" />
              )
            }
            disabled={loading || !isValid}
            form="schedule-post-form"
            className="shadow-lg shadow-purple-500/20"
          >
            {loading ? t("Agendando...") : t("Confirmar Agendamento")}
          </Button>
        </div>
      }
    >
      <form
        id="schedule-post-form"
        onSubmit={handleSubmit}
        className="p-0.5 space-y-6"
      >
        <div className="bg-purple-50/50 border border-purple-200 rounded-2xl p-4 flex items-start gap-4">
          <div className="shrink-0 w-12 h-12 rounded-xl bg-white border border-purple-200 flex items-center justify-center text-purple-600 shadow-xs">
            <Calendar className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-gray-900 mb-1">
              {t("Definir Data e Hora")}
            </h3>
            <p className="text-xs text-gray-500 font-medium leading-relaxed">
              {t("O post")}{" "}
              <span className="text-purple-700 font-bold">#{postId}</span>{" "}
              {t("será publicado automaticamente na data selecionada abaixo.")}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 px-1">
              <Calendar className="w-3.5 h-3.5 text-purple-600" />
              <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
                {t("Data de Envio")}
              </label>
            </div>
            <Input
              type="date"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              min={minDate}
              required
              inputSize="md"
              className="w-full bg-white border-purple-50 focus:border-purple-500 shadow-xs"
            />
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-2 px-1">
              <Clock className="w-3.5 h-3.5 text-purple-600" />
              <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
                {t("Horário")}
              </label>
            </div>
            <Input
              type="time"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              required
              inputSize="md"
              error={timeError}
              className={`w-full bg-white border-purple-50 focus:border-purple-500 shadow-xs ${timeError ? "border-red-300" : ""}`}
            />
          </div>
        </div>

        {formattedDateTime && (
          <div className="relative overflow-hidden bg-linear-to-br from-indigo-600 to-purple-700 rounded-2xl p-5 text-white shadow-lg shadow-purple-500/20">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Clock className="h-16 w-16" />
            </div>
            <div className="relative z-10 flex items-start gap-4">
              <div className="shrink-0 mt-1">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-100 mb-1.5 opacity-80">
                  {t("Confirmação de Agendamento")}
                </p>
                <p className="text-base font-bold capitalize leading-tight">
                  {formattedDateTime}
                </p>
              </div>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default SchedulePostModal;
